import { useGetDocumentWithAllVersions } from "@hooks";
import { Typography } from "@mui/material";
import type { ReactNode } from "react";
import { useMemo } from "react";
import DiffViewer from "src/components/DocViewer/components/markdownDocViewer/diffViewer/DiffViewer";
import { isHumanInTheLoopRejection } from "../../human-in-the-loop";
import type { ChatToolMessage } from "../../types";
import { useChatDeviceContext } from "../../useChatDeviceContext";
import { PendingDocumentToolParameters } from "./PendingDocumentToolParameters";
import { ToolCallFrame } from "./ToolCallFrame";

export interface EditDocumentToolProps {
  args: Record<string, any>;
  toolResult?: ChatToolMessage;
  isLoading?: boolean;
  approvalControls?: ReactNode;
}

const EditDocumentTool = ({
  args,
  toolResult,
  isLoading = false,
  approvalControls,
}: EditDocumentToolProps) => {
  const { orgId } = useChatDeviceContext();
  const documentId =
    typeof args.document_id === "string" ? args.document_id : "";
  const { data: document } = useGetDocumentWithAllVersions(orgId, documentId, {
    throwOnError: false,
  });
  const title = `Edit Document — ${document?.title || documentId || "..."}`;

  // Parse tool result content for replacement count
  const parsedContent = useMemo(() => {
    if (!toolResult?.content || typeof toolResult.content !== "string") {
      return undefined;
    }
    try {
      return JSON.parse(toolResult.content);
    } catch {
      return undefined;
    }
  }, [toolResult?.content]);

  const hasDiff = (args.old_string || "") !== (args.new_string || "");

  const isRejected = isHumanInTheLoopRejection(toolResult);
  const hasError = toolResult?.status === "error" && !isRejected;
  const hasToolResult = !!toolResult?.tool_call_id;

  // Render actions for the frame header
  const renderActions = () => {
    if (isRejected) return null;
    if (hasError) {
      return <Typography className="text-red-500 text-sm">Failed</Typography>;
    }

    if (hasToolResult) {
      return (
        <Typography className="text-green-700 text-sm">Applied</Typography>
      );
    }

    return null;
  };

  return (
    <ToolCallFrame
      title={title}
      isLoading={isLoading && !hasToolResult}
      hasError={hasError}
      isRejected={isRejected}
      isExpandable={!isRejected}
      expandedByDefault={!isRejected}
      hasToolResult={hasToolResult}
      hasContent={hasDiff || hasError}
      actions={renderActions()}
      footer={!hasToolResult && approvalControls}
    >
      {isRejected ? null : hasError ? (
        <Typography className="text-red-500 text-sm">
          {typeof toolResult?.content === "string"
            ? toolResult.content
            : "The edit could not be applied."}
        </Typography>
      ) : (
        <div className="space-y-3">
          {approvalControls && <PendingDocumentToolParameters args={args} />}
          <DiffViewer
            currentMarkdown={args.new_string || ""}
            previousMarkdown={args.old_string || ""}
          />
          {args.replace_all &&
            typeof parsedContent === "object" &&
            parsedContent !== null &&
            ("replacement_count" in parsedContent ||
              "replacementCount" in parsedContent) && (
              <p className="text-sm text-gray-500 my-0">
                Occurrences replaced:{" "}
                {String(
                  parsedContent.replacement_count ??
                    parsedContent.replacementCount
                )}
              </p>
            )}
        </div>
      )}
    </ToolCallFrame>
  );
};

export default EditDocumentTool;

EditDocumentTool.displayName = "EditDocumentTool";
